/**
 * Canonical button primitive — composes design tokens + brand CSS vars.
 *
 *   <Button variant="primary" size="lg">Save</Button>
 *   <Button variant="secondary" size="md">Cancel</Button>
 *   <Button variant="danger" size="sm" icon={<Trash />}>Delete</Button>
 *   <Button variant="primary" size="md" loading>Saving…</Button>
 *   <Button variant="ghost" size="xs" radius="full">All</Button>   // pill filter
 *   <Button iconOnly aria-label="Edit camp" icon={<Edit />} />     // square, named
 *
 * Drop-in replacement for the dominant button patterns found in the
 * 2026-06-01 audit. See `utils/designTokens.ts` for the size/variant
 * source-of-truth.
 */

import React from "react";
import {
  TOKENS,
  type ButtonSize,
  type ButtonVariant,
  type ButtonRadius,
} from "../../utils/designTokens";

interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Color treatment. Defaults to `primary` (brand color). */
  variant?: ButtonVariant;
  /** Padding + font size bucket. Defaults to `md`. */
  size?: ButtonSize;
  /**
   * Corner radius. Defaults to `md`. Use `full` for pill-shaped segmented
   * filters.
   *
   * A prop rather than a className override, for the same reason Card has
   * `padding="none"`: Tailwind resolves same-specificity utilities by
   * stylesheet SOURCE order and .rounded-md is emitted AFTER .rounded-full,
   * so `className="rounded-full"` loses and the button silently stays square.
   */
  radius?: ButtonRadius;
  /**
   * If true, replaces the button content with a spinner + the original
   * label, and disables clicks. Use for async actions.
   */
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: React.ReactNode;
  /** Icon rendered after the label. */
  trailingIcon?: React.ReactNode;
  /** Take the full width of the parent. */
  fullWidth?: boolean;
  /** Children = button label. */
  children?: React.ReactNode;
}

/**
 * `iconOnly` swaps the horizontal size bucket for square padding — `px-4 py-2`
 * around a lone icon renders an oblong control, which is why 221 of these are
 * still hand-rolled across the app.
 *
 * It also REQUIRES an accessible name, enforced here rather than by review.
 * Audit of 215 icon-only buttons: 56 carry their own aria-label/title, 58 are
 * named only because a <Tooltip> ancestor stamps title= onto its child at
 * runtime, and 101 are announced by a screen reader as nothing but "button".
 * Making the label part of the type means the sweep cannot reintroduce a
 * nameless one, and an explicit label also stops the name depending on Tooltip
 * still being present.
 */
type ButtonProps =
  | (BaseButtonProps & { iconOnly: true; "aria-label": string })
  | (BaseButtonProps & { iconOnly?: false });

const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = "primary",
    size = "md",
    radius = "md",
    loading = false,
    iconOnly = false,
    icon,
    trailingIcon,
    fullWidth = false,
    disabled,
    className = "",
    children,
    ...rest
  } = props as BaseButtonProps & { iconOnly?: boolean };

  const classes = [
    TOKENS.button.base,
    TOKENS.button.radius[radius],
    iconOnly ? TOKENS.button.iconSizes[size] : TOKENS.button.sizes[size],
    TOKENS.button.variants[variant],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={classes}
    >
      {loading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : (
        icon
      )}
      {children}
      {!loading && trailingIcon}
    </button>
  );
};

export default Button;
